import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardList, Download, Loader2, Pencil, Plus, RefreshCw, Save, Trash2, X,
} from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CdrTablePagination from "@/components/cdr/CdrTablePagination";
import CdrRecordingPicker from "@/components/cdr/CdrRecordingPicker";
import useCdrStagingBasket from "@/hooks/useCdrStagingBasket";
import SearchableAgentSelect from "@/components/common/SearchableAgentSelect";
import {
  TlRecordingAudioModal,
  TlRecordingUrlActions,
  buildReportRecordingInfo,
} from "@/components/reports/tlRecordingAudio";
import {
  PanelTableActions, PanelTableBody, PanelTableCell, PanelTableEmpty, PanelTableHead,
  PanelTableHeadRow, PanelTableRow, TableHeader,
} from "@/components/ui/panel-table";
import TlIsoWeekPicker from "@/components/reports/TlIsoWeekPicker";
import { getCurrentIsoWeekContext, getIsoWeekAndYear, resolveIsoYearForRow } from "@/utils/isoWeek";
import {
  buildReportListWeekDateParams,
  formatReportDate,
  REPORT_LIST_FILTER_ALL,
  REPORT_LIST_FILTER_DATE,
  REPORT_LIST_FILTER_WEEK,
  resolveLastWeekNumber,
  sanitizeExcelSheetName,
} from "@/utils/tlRecordingReportHelpers";
import {
  agentMetaPatchFromAgent,
  agentsForSearchSelect,
  buildExcelRows,
  createDraftRow,
  defaultWeekDateFieldsForNewRow,
  EMPTY_ROW,
  formatOnayLabel,
  formatWeekRangeLabel,
  formRowToPayload,
  getReportConfig,
  getReportColumns,
  mapItemToForm,
  truncateReportText,
  consumeKalitePrefillFromTl,
  managerReportHasQualityPersonnelFilter,
} from "@/utils/managerReportHelpers";

const PAGE_SIZE = 25;
const ONAY_SELECT_EMPTY = "__empty__";

const findAgentById = (agents, id) => (agents || []).find((a) => String(a.id) === String(id));

const MgrCellEditor = ({
  col, row, isEditing, agents, leaders, qualityUsers, searchableAgents,
  isDark, inputClass, labelClass, apiBase, onPlayRecording, onFieldChange, onFieldsChange,
  onOpenPicker, pickerContext, cdrBasketCount, contextIsoYear,
}) => {
  const value = row[col.key] ?? "";
  const cellInput = `${inputClass} h-8 text-xs`;
  const cellTextarea = `${inputClass} text-xs min-h-[72px] resize-y`;

  const renderReadonly = () => {
    if (col.type === "yesno") {
      const label = formatOnayLabel(value);
      return (
        <span className={`text-[11px] ${label ? "" : labelClass}`}>{label || "—"}</span>
      );
    }
    if (col.type === "recording_url") {
      return (
        <TlRecordingUrlActions
          url={value}
          apiBase={apiBase}
          isDark={isDark}
          labelClass={labelClass}
          onPlay={(u) => onPlayRecording(u, `${col.label} · ${row.assistant_name || ""}`)}
        />
      );
    }
    if (col.type === "text") {
      const text = String(value || "").trim();
      if (!text) return <span className={labelClass}>—</span>;
      return <span className="text-[11px] line-clamp-3" title={text}>{truncateReportText(text, 100)}</span>;
    }
    if (col.type === "leader_select") return <span className="text-[11px]">{row.leader_name || "—"}</span>;
    if (col.type === "quality_select") return <span className="text-[11px]">{row.quality_user_name || "—"}</span>;
    if (col.type === "agent_select") return <span className="text-[11px] font-medium">{row.assistant_name || "—"}</span>;
    if (col.type === "week_display") {
      if (!row?.week_number && row?.week_number !== 0 && !row?.report_date) {
        return <span className={labelClass}>—</span>;
      }
      const isoYear = resolveIsoYearForRow(row, contextIsoYear);
      const rangeLabel = formatWeekRangeLabel(isoYear, Number(row.week_number));
      return (
        <span className={`text-[11px] whitespace-nowrap ${isDark ? "text-slate-200" : "text-gray-800"}`}>
          {rangeLabel || "—"}
        </span>
      );
    }
    if (col.type === "date" || col.key === "yeni_dataya_gecis_tarihi") {
      return <span className={`text-[11px] tabular-nums ${value ? "" : labelClass}`}>{formatReportDate(value)}</span>;
    }
    return <span className={`text-[11px] ${value ? "" : labelClass}`}>{value || "—"}</span>;
  };

  if (!isEditing) return renderReadonly();

  if (col.type === "leader_select") {
    return (
      <Select
        value={value ? String(value) : ""}
        onValueChange={(v) => {
          const leader = leaders.find((l) => String(l.id) === v);
          onFieldsChange({
            leader_user_id: v,
            leader_name: leader?.name || "",
          });
        }}
      >
        <SelectTrigger className={`h-8 text-xs min-w-[140px] ${inputClass}`}><SelectValue placeholder="Lider" /></SelectTrigger>
        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
          {leaders.map((l) => <SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }

  if (col.type === "quality_select") {
    return (
      <Select
        value={value ? String(value) : ""}
        onValueChange={(v) => {
          const qu = qualityUsers.find((q) => String(q.id) === v);
          onFieldsChange({
            quality_user_id: v,
            quality_user_name: qu?.name || "",
          });
        }}
      >
        <SelectTrigger className={`h-8 text-xs min-w-[140px] ${inputClass}`}><SelectValue placeholder="Kalite" /></SelectTrigger>
        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
          {qualityUsers.map((q) => <SelectItem key={q.id} value={String(q.id)}>{q.name}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }

  if (col.type === "agent_select") {
    const selectedAgent = searchableAgents.find((a) => String(a.id) === String(row.agent_id))
      || (row.assistant_name ? { id: row.agent_id, ad_soyad: row.assistant_name } : null);
    return (
      <SearchableAgentSelect
        agents={searchableAgents}
        value={selectedAgent}
        onChange={(agent) => {
          const full = findAgentById(agents, agent?.id);
          onFieldsChange(agentMetaPatchFromAgent(full || agent));
        }}
        placeholder="Asistan"
        isDark={isDark}
        inputClass={inputClass}
        labelClass={labelClass}
        className="w-full min-w-[140px]"
      />
    );
  }

  if (col.type === "readonly") {
    return renderReadonly();
  }

  if (col.type === "week_display") {
    const isoYear = resolveIsoYearForRow(row, contextIsoYear);
    const rawWeek = row.week_number === "" || row.week_number == null
      ? resolveLastWeekNumber([], [])
      : Number(row.week_number);
    const weekVal = Number.isFinite(rawWeek) && rawWeek >= 1 ? rawWeek : resolveLastWeekNumber([], []);
    return (
      <TlIsoWeekPicker
        compact
        isoYear={isoYear}
        week={weekVal}
        isDark={isDark}
        inputClass={inputClass}
        allLabel="Hafta seçin"
        onChange={(payload) => {
          if (payload.cleared) return;
          onFieldsChange({ week_number: payload.week, report_date: payload.start });
        }}
      />
    );
  }

  if (col.type === "date") {
    return (
      <Input
        type="date"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) {
            onFieldChange(col.key, "");
            return;
          }
          const { week } = getIsoWeekAndYear(new Date(v.replace(/-/g, "/")));
          onFieldsChange({ report_date: v, week_number: week });
        }}
        className={cellInput}
      />
    );
  }

  if (col.type === "yesno") {
    const selectValue = value === "evet" || value === "hayir" ? value : ONAY_SELECT_EMPTY;
    return (
      <Select value={selectValue} onValueChange={(v) => onFieldChange(col.key, v === ONAY_SELECT_EMPTY ? "" : v)}>
        <SelectTrigger className={`h-8 text-[11px] w-[100px] ${inputClass}`}><SelectValue placeholder="Seçiniz" /></SelectTrigger>
        <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
          <SelectItem value={ONAY_SELECT_EMPTY}>Seçiniz</SelectItem>
          <SelectItem value="evet">Evet</SelectItem>
          <SelectItem value="hayir">Hayır</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (col.type === "text") {
    return (
      <Textarea
        rows={3}
        value={value}
        onChange={(e) => onFieldChange(col.key, e.target.value)}
        className={cellTextarea}
        placeholder={col.label}
      />
    );
  }

  if (col.type === "recording_url") {
    const canPick = cdrBasketCount > 0;
    return (
      <div className="flex gap-1 min-w-[120px]">
        <Input
          value={value}
          onChange={(e) => onFieldChange(col.key, e.target.value)}
          className={`${cellInput} font-mono flex-1 min-w-0`}
          placeholder="https://..."
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={!canPick}
          className="h-8 w-8 shrink-0"
          title={canPick ? "CDR sepetinden seç" : "CDR sepeti boş"}
          onClick={() => {
            if (!canPick) {
              toast.warning("CDR sepeti boş. Ana sayfadaki çağrı kayıtlarından sepete ekleyin.");
              return;
            }
            onOpenPicker(pickerContext, col.key, row);
          }}
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  return renderReadonly();
};

const ManagerQualityReportsPage = ({
  apiBase, isDark, textClass, labelClass, reportType,
}) => {
  const config = getReportConfig(reportType);
  const columns = useMemo(() => getReportColumns(reportType), [reportType]);
  const emptyForm = EMPTY_ROW[reportType] || {};

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [agents, setAgents] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [qualityUsers, setQualityUsers] = useState([]);
  const [assistantFilter, setAssistantFilter] = useState(null);
  const [leaderFilter, setLeaderFilter] = useState("");
  const [qualityFilter, setQualityFilter] = useState("");
  const [listFilterMode, setListFilterMode] = useState(REPORT_LIST_FILTER_ALL);
  const [filterIsoYear, setFilterIsoYear] = useState(() => getCurrentIsoWeekContext().isoYear);
  const [filterWeek, setFilterWeek] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [draftRows, setDraftRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editBuffer, setEditBuffer] = useState(emptyForm);
  const [rowSavingId, setRowSavingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null);
  const [audioPreview, setAudioPreview] = useState({ open: false, url: "", title: "", recordInfo: null });
  const cdrStaging = useCdrStagingBasket();
  const tlPrefillPendingRef = useRef(null);
  const tlPrefillAppliedRef = useRef(false);

  const panelClass = isDark ? "border-slate-800 bg-slate-900/60" : "border-gray-200 bg-white";
  const inputClass = isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-gray-200";
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const assistantSearch = assistantFilter?.ad_soyad?.trim() || "";
  const searchableAgents = useMemo(() => agentsForSearchSelect(agents), [agents]);
  const agentOptions = useMemo(() => agents.map((a) => ({ id: a.id, name: a.name || a.ad_soyad })), [agents]);

  const apiParams = useMemo(() => ({ report_type: reportType }), [reportType]);

  const mgrListQueryParams = useMemo(() => ({
    search: assistantSearch || undefined,
    leader_user_id: leaderFilter || undefined,
    quality_user_id: managerReportHasQualityPersonnelFilter(reportType) && qualityFilter ? qualityFilter : undefined,
    agent_id: assistantFilter?.id || undefined,
    ...buildReportListWeekDateParams({
      filterMode: listFilterMode,
      filterWeek,
      startDate,
      endDate,
    }),
  }), [assistantSearch, leaderFilter, qualityFilter, reportType, assistantFilter?.id, listFilterMode, filterWeek, startDate, endDate]);

  const contextIsoYear = listFilterMode === REPORT_LIST_FILTER_WEEK ? filterIsoYear : getCurrentIsoWeekContext().isoYear;

  const loadMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/manager-quality-reports`, {
        params: { ...apiParams, action: "meta" },
      });
      setAgents(res.data?.agents || []);
      setLeaders(res.data?.leaders || []);
      setQualityUsers(res.data?.quality_users || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Meta yüklenemedi");
    }
  }, [apiBase, apiParams]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/manager-quality-reports`, {
        params: {
          ...apiParams,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          ...mgrListQueryParams,
        },
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rapor yüklenemedi");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [apiBase, apiParams, page, mgrListQueryParams]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => {
    setEditBuffer(EMPTY_ROW[reportType] || {});
    setDraftRows([]);
    setEditingId(null);
    setPage(1);
    setLeaderFilter("");
    setQualityFilter("");
    setListFilterMode(REPORT_LIST_FILTER_ALL);
    setFilterWeek("");
    setStartDate("");
    setEndDate("");
    setFilterIsoYear(getCurrentIsoWeekContext().isoYear);
    tlPrefillPendingRef.current = null;
    tlPrefillAppliedRef.current = false;
  }, [reportType]);

  useEffect(() => {
    if (reportType !== "kalite-degerlendirme" || tlPrefillAppliedRef.current) return;
    const draft = consumeKalitePrefillFromTl();
    if (draft) tlPrefillPendingRef.current = draft;
  }, [reportType]);

  useEffect(() => {
    if (reportType !== "kalite-degerlendirme" || tlPrefillAppliedRef.current) return;
    const pending = tlPrefillPendingRef.current;
    if (!pending) return;
    if (pending.agent_id && agents.length === 0) return;
    const agent = findAgentById(agents, pending.agent_id);
    const enriched = {
      ...pending,
      ...(agent ? agentMetaPatchFromAgent(agent) : {}),
    };
    tlPrefillAppliedRef.current = true;
    tlPrefillPendingRef.current = null;
    setDraftRows((prev) => {
      if (prev.some((d) => d._source_tl_recording_id === enriched._source_tl_recording_id)) {
        return prev;
      }
      return [enriched, ...prev];
    });
    if (enriched.report_date) {
      setListFilterMode(REPORT_LIST_FILTER_DATE);
      setStartDate(String(enriched.report_date).slice(0, 10));
      setEndDate(String(enriched.report_date).slice(0, 10));
      setFilterWeek("");
      setPage(1);
    }
    toast.success(
      "Tk. lideri ses kaydından aktarıldı: asistan, lider, hafta, tarih ve 1./2. görüşme kayıtları hazır. Kalite personeli alanları doldurup kaydedebilir.",
    );
  }, [reportType, agents]);

  const validateRow = (row) => {
    if (!row.agent_id) {
      toast.error("Asistan seçimi zorunlu");
      return false;
    }
    if (!row.leader_user_id) {
      toast.error("Takım lideri zorunlu");
      return false;
    }
    if (managerReportHasQualityPersonnelFilter(reportType) && !row.quality_user_id) {
      toast.error("Kalite kullanıcısı zorunlu");
      return false;
    }
    if (!String(row.report_date || "").trim()) {
      toast.error("Tarih zorunlu");
      return false;
    }
    return true;
  };

  const addNewRow = () => {
    setDraftRows((prev) => [createDraftRow(reportType, defaultWeekDateFieldsForNewRow()), ...prev]);
  };

  const updateDraftRow = (draftId, key, valueOrFields) => {
    setDraftRows((prev) => prev.map((row) => {
      if (row._draftId !== draftId) return row;
      if (key === null && valueOrFields && typeof valueOrFields === "object") {
        return { ...row, ...valueOrFields };
      }
      return { ...row, [key]: valueOrFields };
    }));
  };

  const saveDraftRow = async (draftId) => {
    const row = draftRows.find((r) => r._draftId === draftId);
    if (!row || !validateRow(row)) return;
    setRowSavingId(draftId);
    try {
      await axios.post(`${apiBase}/manager-quality-reports`, formRowToPayload(reportType, row), {
        params: apiParams,
      });
      toast.success("Kayıt eklendi");
      setDraftRows((prev) => prev.filter((r) => r._draftId !== draftId));
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setRowSavingId(null);
    }
  };

  const startEdit = (row) => {
    if (editingId && editingId !== row.id) {
      toast.warning("Önce açık düzenlemeyi kaydedin veya iptal edin");
      return;
    }
    setEditingId(row.id);
    setEditBuffer(mapItemToForm(reportType, row));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBuffer(emptyForm);
  };

  const saveEditRow = async () => {
    if (!editingId || !validateRow(editBuffer)) return;
    setRowSavingId(editingId);
    try {
      await axios.put(`${apiBase}/manager-quality-reports?id=${editingId}`, formRowToPayload(reportType, editBuffer), {
        params: apiParams,
      });
      toast.success("Kayıt güncellendi");
      setEditingId(null);
      setEditBuffer(emptyForm);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setRowSavingId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.assistant_name}" kaydı silinsin mi?`)) return;
    try {
      await axios.delete(`${apiBase}/manager-quality-reports?id=${row.id}`, { params: apiParams });
      toast.success("Silindi");
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const openInlinePicker = (context, fieldKey, row) => {
    if (cdrStaging.count <= 0) {
      toast.warning("CDR sepeti boş. Ana sayfadaki çağrı kayıtlarından sepete ekleyin.");
      return;
    }
    setPickerTarget({ ...context, fieldKey, agentId: row.agent_id });
    setPickerOpen(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${apiBase}/manager-quality-reports`, {
        params: {
          ...apiParams,
          action: "export",
          ...mgrListQueryParams,
        },
        timeout: 120000,
      });
      const rows = res.data?.items || [];
      if (rows.length === 0) {
        toast.warning("Dışa aktarılacak kayıt yok");
        return;
      }
      const wb = XLSX.utils.book_new();
      const sheetName = sanitizeExcelSheetName(config?.title || reportType);
      const ws = XLSX.utils.aoa_to_sheet(buildExcelRows(reportType, rows));
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${reportType}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${rows.length} kayıt Excel'e aktarıldı`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Excel dışa aktarılamadı");
    } finally {
      setExporting(false);
    }
  };

  const openRecordingPlayer = (url, title, extra = {}) => {
    const recordingUrl = String(url || "").trim();
    if (!recordingUrl) {
      toast.warning("Ses URL yok");
      return;
    }
    setAudioPreview({
      open: true,
      url: recordingUrl,
      title,
      recordInfo: buildReportRecordingInfo({
        title,
        url: recordingUrl,
        sourceLabel: "Kalite / Yönetici Raporu",
        ...extra,
      }),
    });
  };

  const renderDataRow = (row, { isDraft, draftId, isEditing }) => {
    const data = isDraft ? row : (isEditing ? editBuffer : row);
    const pickerContext = isDraft
      ? { mode: "draft", draftId }
      : { mode: "inline" };

    return (
      <PanelTableRow key={isDraft ? draftId : row.id} className={isDraft ? "bg-violet-500/5" : ""}>
        {columns.map((col) => (
          <PanelTableCell key={col.key} className="align-top min-w-[120px] max-w-[260px]">
            <MgrCellEditor
              col={col}
              row={data}
              isEditing={isDraft || isEditing}
              agents={agents}
              leaders={leaders}
              qualityUsers={qualityUsers}
              searchableAgents={searchableAgents}
              isDark={isDark}
              inputClass={inputClass}
              labelClass={labelClass}
              apiBase={apiBase}
              onPlayRecording={openRecordingPlayer}
              onFieldChange={(key, val) => {
                if (isDraft) updateDraftRow(draftId, key, val);
                else setEditBuffer((prev) => ({ ...prev, [key]: val }));
              }}
              onFieldsChange={(fields) => {
                if (isDraft) updateDraftRow(draftId, null, fields);
                else setEditBuffer((prev) => ({ ...prev, ...fields }));
              }}
              onOpenPicker={openInlinePicker}
              pickerContext={pickerContext}
              cdrBasketCount={cdrStaging.count}
              contextIsoYear={contextIsoYear}
            />
          </PanelTableCell>
        ))}
        <PanelTableCell className="w-[100px] sticky right-0 bg-inherit">
          <PanelTableActions>
            {isDraft ? (
              <>
                <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={rowSavingId === draftId} onClick={() => saveDraftRow(draftId)}>
                  {rowSavingId === draftId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-emerald-500" />}
                </Button>
                <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setDraftRows((prev) => prev.filter((r) => r._draftId !== draftId))}>
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : isEditing ? (
              <>
                <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={rowSavingId === row.id} onClick={saveEditRow}>
                  {rowSavingId === row.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-emerald-500" />}
                </Button>
                <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={cancelEdit}>
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => startEdit(row)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDelete(row)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </PanelTableActions>
        </PanelTableCell>
      </PanelTableRow>
    );
  };

  if (!config) {
    return (
      <div className={`rounded-2xl border p-8 text-center ${panelClass}`}>
        <p className={labelClass}>Geçersiz rapor tipi: {reportType}</p>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border overflow-hidden ${panelClass}`}>
      <div className={`px-4 py-4 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 ${isDark ? "border-slate-800" : "border-gray-100"}`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDark ? "bg-violet-500/15 text-violet-400" : "bg-violet-50 text-violet-600"}`}>
            <ClipboardList className="w-5 h-5" />
          </div>
          <div>
            <h3 className={`text-base font-semibold ${textClass}`}>{config.title}</h3>
            <p className={`text-xs ${labelClass}`}>{config.subtitle}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={addNewRow} className="h-9">
            <Plus className="w-4 h-4 mr-1" /> Satır
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={handleExport} disabled={exporting || loading} className={isDark ? "border-slate-700" : ""}>
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Excel
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={loadItems} disabled={loading} className={isDark ? "border-slate-700" : ""}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className={`px-4 py-3 border-b flex flex-col xl:flex-row gap-2 ${isDark ? "border-slate-800 bg-slate-950/20" : "border-gray-100 bg-gray-50/70"}`}>
        <div className="min-w-[200px] flex-1 max-w-xs">
          <SearchableAgentSelect
            agents={searchableAgents}
            value={assistantFilter}
            onChange={(agent) => { setAssistantFilter(agent); setPage(1); }}
            placeholder="Asistan ara…"
            isDark={isDark}
            inputClass={inputClass}
            labelClass={labelClass}
          />
        </div>
        <TlIsoWeekPicker
          isoYear={filterIsoYear}
          week={listFilterMode === REPORT_LIST_FILTER_WEEK ? filterWeek : ""}
          isDark={isDark}
          inputClass={inputClass}
          onChange={(payload) => {
            if (payload.cleared) {
              setListFilterMode(REPORT_LIST_FILTER_ALL);
              setFilterWeek("");
              setStartDate("");
              setEndDate("");
              setPage(1);
              return;
            }
            setListFilterMode(REPORT_LIST_FILTER_WEEK);
            setFilterIsoYear(payload.isoYear);
            setFilterWeek(payload.week);
            setStartDate("");
            setEndDate("");
            setPage(1);
          }}
        />
        <Input
          type="date"
          value={listFilterMode === REPORT_LIST_FILTER_DATE ? startDate : ""}
          onChange={(e) => {
            const v = e.target.value;
            setStartDate(v);
            if (v || endDate) {
              setListFilterMode(REPORT_LIST_FILTER_DATE);
              setFilterWeek("");
            } else if (!v && !endDate) {
              setListFilterMode(REPORT_LIST_FILTER_ALL);
            }
            setPage(1);
          }}
          className={`h-9 md:w-36 ${inputClass}`}
          title="Başlangıç tarihi"
        />
        <Input
          type="date"
          value={listFilterMode === REPORT_LIST_FILTER_DATE ? endDate : ""}
          onChange={(e) => {
            const v = e.target.value;
            setEndDate(v);
            if (startDate || v) {
              setListFilterMode(REPORT_LIST_FILTER_DATE);
              setFilterWeek("");
            } else if (!v && !startDate) {
              setListFilterMode(REPORT_LIST_FILTER_ALL);
            }
            setPage(1);
          }}
          className={`h-9 md:w-36 ${inputClass}`}
          title="Bitiş tarihi"
        />
        {leaders.length > 0 && (
          <select
            value={leaderFilter}
            onChange={(e) => { setLeaderFilter(e.target.value); setPage(1); }}
            className={`h-9 rounded-md border px-2 text-sm min-w-[180px] ${inputClass}`}
          >
            <option value="">Tüm liderler</option>
            {leaders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
        {managerReportHasQualityPersonnelFilter(reportType) && qualityUsers.length > 0 && (
          <select
            value={qualityFilter}
            onChange={(e) => { setQualityFilter(e.target.value); setPage(1); }}
            className={`h-9 rounded-md border px-2 text-sm min-w-[180px] ${inputClass}`}
          >
            <option value="">Tüm kalite personeli</option>
            {qualityUsers.map((q) => (
              <option key={q.id} value={q.id}>{q.name}</option>
            ))}
          </select>
        )}
        <Button type="button" size="sm" className="h-9" onClick={() => { setPage(1); loadItems(); }}>Filtrele</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="panel-table caption-bottom text-sm w-max min-w-full border-collapse">
            <TableHeader>
              <PanelTableHeadRow>
                {columns.map((col) => (
                  <PanelTableHead key={col.key} className="text-[11px] whitespace-nowrap">{col.label}</PanelTableHead>
                ))}
                <PanelTableHead className="w-[100px] sticky right-0">İşlem</PanelTableHead>
              </PanelTableHeadRow>
            </TableHeader>
            <PanelTableBody>
              {draftRows.map((row) => renderDataRow(row, { isDraft: true, draftId: row._draftId, isEditing: false }))}
              {items.map((row) => renderDataRow(row, {
                isDraft: false,
                isEditing: editingId === row.id,
              }))}
              {draftRows.length === 0 && items.length === 0 && (
                <PanelTableEmpty colSpan={columns.length + 1} message="Kayıt yok — Satır ile yeni kayıt ekleyin" />
              )}
            </PanelTableBody>
          </table>
        </div>
      )}

      <CdrTablePagination isDark={isDark} page={page} pageSize={PAGE_SIZE} total={total} totalPages={totalPages} loading={loading} onPageChange={setPage} />

      <TlRecordingAudioModal
        open={audioPreview.open}
        url={audioPreview.url}
        title={audioPreview.title}
        onClose={() => setAudioPreview({ open: false, url: "", title: "", recordInfo: null })}
        isDark={isDark}
        labelClass={labelClass}
        apiBase={apiBase}
        recordInfo={audioPreview.recordInfo}
      />

      <CdrRecordingPicker
        apiBase={apiBase}
        open={pickerOpen}
        basketOnly
        onClose={() => { setPickerOpen(false); setPickerTarget(null); }}
        agentId={pickerTarget?.agentId || undefined}
        fieldLabel={columns.find((c) => c.key === pickerTarget?.fieldKey)?.label || ""}
        agents={agentOptions}
        isDark={isDark}
        onSelect={(url) => {
          if (!pickerTarget) return;
          if (pickerTarget.mode === "draft") {
            setDraftRows((prev) => prev.map((row) => (
              row._draftId === pickerTarget.draftId
                ? { ...row, [pickerTarget.fieldKey]: url }
                : row
            )));
          } else if (pickerTarget.mode === "inline") {
            setEditBuffer((f) => ({ ...f, [pickerTarget.fieldKey]: url }));
          }
        }}
      />
    </div>
  );
};

export default ManagerQualityReportsPage;
